<template>
  <section
    class="flex flex-col lg:flex-row gap-4 lg:gap-8 w-full"
    :data-testid="dataTestid"
  >
    <div
      class="post-tile-group-content"
      :class="{
        'w-full': !($slots.sidebar && type !== 'sidebar')
      }"
    >
      <div
        class="flex flex-col md:flex-row gap-4 justify-between w-full"
        :class="titleClass"
      >
        <V2AtomsHighlight
          v-if="title"
          ref="title"
          class="title font-semibold"
          :size="titleSize"
        >
          {{ title }}
        </V2AtomsHighlight>

        <div
          v-if="showSeeAll"
          class="items-center hidden md:flex"
        >
          <V2AtomsButton
            theme="secondary"
            size="x-small"
            has-icon-right
            :to="seeAllLink"
          >
            {{ seeAllLabel }}
          </V2AtomsButton>
        </div>
      </div>

      <div
        :class="{
          'lg:grid lg:grid-cols-3 lg:gap-6': $slots['subscription-form']
        }"
      >
        <V2AtomsBaseCard
          v-if="!_isEmpty(featuredItem)"
          size="small"
          class="max-w-full mb-10 border-2 border-semantic-util-transparent shadow-combined-elevation-1 hover:border-2 hover:!border-semantic-primary-default"
          :class="{
            'lg:col-span-2': $slots['subscription-form']
          }"
        >
          <div class="flex-col md:flex-row flex items-center h-full">
            <div
              v-if="featuredItem.image"
              class="md:mr-6"
              :class="imageWrapperClass"
            >
              <img
                :src="featuredItem.image"
                class="rounded-2xl w-full h-full object-cover"
                :class="imageClass"
                alt="Card"
              />
            </div>
            <div
              class="flex flex-col text-left gap-1 md:gap-2 pt-4 md:pt-0"
            >
              <template v-if="enableBadge">
                <V2AtomsLabel :theme="featureBadgeTheme">
                  {{ badgeTitle || featuredItem.tag }}
                </V2AtomsLabel>
              </template>

              <!-- eslint-disable vue/no-v-html -->
              <div
                v-if="featuredItem.title"
                class="font-semibold text-lg/[120%] md:text-2xl/[120%] text-semantic-surface-on-surface line-clamp-3 text-ellipsis md:mt-2 mt-1"
                v-html="featuredItem.title"
              />

              <NuxtLink
                v-if="featuredItem.authorName"
                :to="trailingSlash(prefixAuthorUrl + (featuredItem.authorUrl ?? featuredItem.url))"
                class="font-semibold text-2xs/[120%] md:text-xs/[120%] text-semantic-surface-on-surface"
              >
                by <span class="underline font-bold">{{ featuredItem.authorName }}</span>
              </NuxtLink>

              <div
                v-if="featuredItem.content"
                class="md:text-lg/[150%] text-base/[150%] text-semantic-surface-on-surface text-ellipsis line-clamp-3 min-h-[5rem]"
                v-html="featuredItem.content"
              />

              <div class="flex justify-end items-end grow">
                <V2AtomsButton
                  has-icon-right
                  theme="tertiary"
                  :to="featuredItem.url"
                  size="small"
                >
                  Read more
                </V2AtomsButton>
              </div>
            </div>
          </div>
        </V2AtomsBaseCard>

        <div v-if="$slots['subscription-form']" class="lg:col-span-1 lg:block hidden lg:mb-10">
          <slot name="subscription-form" />
        </div>
      </div>

      <div
        v-if="type === 'sidebar'"
        :class="[
          component.main,
          tileClass
        ]"
      >
        <MoleculesPostTile
          v-for="(item, itemIndex) in items"
          :key="`post-tile-${itemIndex}`"
          v-bind="item"
          :type="type"
          :layout="layout"
          :has-border="hasBorder"
          :border-class="borderClass"
          :has-divider="hasDivider"
          :prefix-author-url="prefixAuthorUrl"
          :show-author="showAuthor"
          :open-in-new-tab="openInNewTab"
          :vertical-align="alignCenter"
        />
      </div>

      <div
        v-else
        class="grid gap-8"
        :class="`grid-cols-1 md:grid-cols-${itemsPerRow}`"
      >
        <V2MoleculesCardArticleCard
          v-for="(item, itemIndex) in items"
          :key="itemIndex"
          cta-btn-label="Read more"
          wrapper-class="!max-w-none border-2 border-semantic-util-transparent shadow-combined-elevation-1"
          inner-wrapper-body-class="pt-4 !mt-0"
          :badge-theme="badgeTheme"
          :title="item.title"
          :description="item.content"
          :author-name="item.authorName"
          :author-link="trailingSlash(prefixAuthorUrl + item.authorUrl)"
          :image-path="item.image"
          :cta-btn-to="item.url"
          :badge-title="item.tag"
          :show-author="showAuthor"
        />
      </div>

      <div
        v-if="showSeeAll"
        class="items-center justify-end flex md:hidden mt-8 ml-auto"
      >
        <V2AtomsButton
          theme="secondary"
          size="x-small"
          has-icon-right
          :to="seeAllLink"
        >
          {{ seeAllLabel }}
        </V2AtomsButton>
      </div>

      <!-- subscription form for mobile - appears after article grid -->
      <div v-if="$slots['subscription-form']" class="lg:hidden mt-10">
        <slot name="subscription-form" />
      </div>

      <div
        v-if="hasLoadMore"
        :class="{
          'mb-6 md:mb-16': title === 'Top News',
          'mb-10': type !== 'sidebar'
        }"
      >
        <AtomsButton
          ref="loadmore"
          theme="link"
          size="large"
          class="text-center mx-auto mt-6 md:mt-10 !p-0 !text-base md:!text-lg underline"
          @click="handleLoadMore"
        >
          Load more

          <IconsChevron
            type="default"
            size="lg"
            direction="bottom"
            class="fill-semantic-primary-default ml-2.5 group-active:fill-semantic-primary-variant-light group-hover:fill-semantic-primary-variant-light"
          />
        </AtomsButton>
      </div>

      <hr
        v-if="hasContentDivider"
        class="border-b border-b-gray-400"
        :class="contentDividerClass"
      />
    </div>

    <div
      v-if="$slots.sidebar && type !== 'sidebar'"
      class="post-tile-group-sidebar min-w-[16rem] lg:max-w-[16rem] flex flex-col gap-8 md:gap-6"
    >
      <slot name="sidebar" />
    </div>
  </section>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

import {
  DEFAULT_AUTHOR_URL
} from '~/constants/pages/contentful'

defineOptions({
  name: 'V2MoleculesPostTileGroup'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: 'post-tile-group'
  },

  title: {
    type: String,
    default: '',
    validator: value => !checkStringContainsHtml(value)
  },

  titleSize: {
    type: String,
    default: 'base',
    validator: value => ['base', 'large'].includes(value)
  },

  titleClass: {
    type: String,
    default: ''
  },

  featuredItem: {
    type: Object,
    default: () => ({})
  },

  items: {
    type: Array,
    default: () => ([])
  },

  itemsPerRow: {
    type: [Number, String],
    default: 3,
    validator: value => [1, 3, 4].includes(value)
  },

  layout: {
    type: String,
    default: 'vertical',
    validator: value => ['horizontal', 'horizontal-small', 'vertical', 'small-tile'].includes(value)
  },

  type: {
    type: String,
    default: 'expert-analysis',
    validator: value => ['expert-analysis', 'perks', 'sidebar'].includes(value)
  },

  paddingSize: {
    type: String,
    default: 'default',
    validator: value => ['default', 'small'].includes(value)
  },

  hasBorder: {
    type: Boolean,
    default: false
  },

  borderClass: {
    type: String,
    default: ''
  },

  hasDivider: {
    type: [Boolean, Number],
    default: false
  },

  dividerClass: {
    type: String,
    default: ''
  },

  hasLoadMore: {
    type: Boolean,
    default: false
  },

  hasContentDivider: {
    type: Boolean,
    default: false
  },

  contentDividerClass: {
    type: String,
    default: ''
  },

  showAuthor: {
    type: Boolean,
    default: true
  },

  prefixAuthorUrl: {
    type: String,
    default: DEFAULT_AUTHOR_URL
  },

  tileClass: {
    type: String,
    default: ''
  },

  openInNewTab: {
    type: Boolean,
    default: false
  },

  enableBadge: {
    type: Boolean,
    default: false
  },

  badgeTheme: {
    type: String,
    default: 'outline',
    validator: value => ['default', 'outline', 'inverted'].includes(value)
  },

  featureBadgeTheme: {
    type: String,
    default: 'default',
    validator: value => ['default', 'outline', 'inverted'].includes(value)
  },

  badgeTitle: {
    type: String,
    default: ''
  },

  imageClass: {
    type: String,
    default: ''
  },

  imageWrapperClass: {
    type: String,
    default: ''
  },

  alignCenter: {
    type: Boolean,
    default: false
  },

  initialItemsCount: {
    type: Number,
    default: null
  },

  showSeeAll: {
    type: Boolean,
    default: false
  },

  seeAllLabel: {
    type: String,
    default: 'See all articles'
  },

  seeAllLink: {
    type: String,
    default: ''
  }
})

const component = computed(() => {
  const type = props.type
  const itemsPerRow = props.itemsPerRow
  const paddingSize = props.paddingSize

  const main = (() => {
    let common = getKey(type, {
      'expert-analysis': `grid md:gap-8 md:grid-cols-2 ${getKey(paddingSize, {
        default: 'gap-4',
        small: 'gap-2'
      })}`,
      perks: 'grid gap-4 sm:grid-cols-2 sm:gap-6 md:gap-8',
      sidebar: 'flex flex-col'
    })

    if (['perks', 'expert-analysis'].includes(type)) {
      common += getKey(itemsPerRow, {
        3: ' lg:grid-cols-3',
        4: ' lg:grid-cols-4'
      })
    }

    return common
  })()

  return {
    main
  }
})

const emit = defineEmits(['loadMore'])

function handleLoadMore () {
  emit('loadMore', true)
}
</script>
